// Turns an image, emoji or custom icon into nonogram cells. Every source becomes a canvas; it's
// drawn under a transform onto a supersampled grid and each cell's average darkness is thresholded.

const SOURCE_SIZE = 512;
const MAX_IMAGE_SIDE = 1024;
const SAMPLES = 12;

const makeCanvas = (w, h) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w));
  canvas.height = Math.max(1, Math.round(h));
  return canvas;
};

// Crops a transparent-background drawing to its visible pixels, so it fits the grid snugly.
const cropToInk = (canvas) => {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 16) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas;
  const out = makeCanvas(maxX - minX + 1, maxY - minY + 1);
  out.getContext('2d').drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Kuvan lataus epäonnistui'));
  img.src = src;
});

const imageToCanvas = (img, maxSide) => {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const canvas = makeCanvas((img.naturalWidth || img.width) * scale, (img.naturalHeight || img.height) * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

export const sourceFromBlob = async (blob) => {
  const url = URL.createObjectURL(blob);
  try {
    const canvas = imageToCanvas(await loadImage(url), MAX_IMAGE_SIDE);
    return hasTransparency(canvas) ? cropToInk(canvas) : canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const hasTransparency = (canvas) => {
  const data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4 * 7) if (data[i] < 200) return true;
  return false;
};

export const sourceFromSvg = async (svgText) => {
  // Without a size an SVG may draw at 0×0 or 150×150, so force a large render size.
  const sized = svgText.replace(/<svg\b([^>]*)>/i, (tag, attrs) => {
    const clean = attrs.replace(/\s(width|height)\s*=\s*"[^"]*"/gi, '');
    return `<svg${clean} width="${SOURCE_SIZE}" height="${SOURCE_SIZE}">`;
  });
  const blob = new Blob([sized], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = makeCanvas(SOURCE_SIZE, SOURCE_SIZE);
    canvas.getContext('2d').drawImage(img, 0, 0, SOURCE_SIZE, SOURCE_SIZE);
    return cropToInk(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const sourceFromEmoji = async (emoji) => {
  const font = `${Math.round(SOURCE_SIZE * 0.8)}px "Noto Emoji"`;
  try { await document.fonts.load(font, emoji); } catch { /* falls back to the system emoji font */ }
  const canvas = makeCanvas(SOURCE_SIZE, SOURCE_SIZE);
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#000';
  ctx.fillText(emoji, SOURCE_SIZE / 2, SOURCE_SIZE / 2);
  return cropToInk(canvas);
};

export const sourceFromSymbol = async (name) => {
  const font = `${Math.round(SOURCE_SIZE * 0.8)}px "Material Symbols Outlined"`;
  try { await document.fonts.load(font, name); } catch { /* font unavailable */ }
  const canvas = makeCanvas(SOURCE_SIZE, SOURCE_SIZE);
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#000';
  ctx.fillText(name, SOURCE_SIZE / 2, SOURCE_SIZE / 2);
  return cropToInk(canvas);
};

// Fills every area the drawing encloses, so outline art (Noto Emoji, line icons) becomes a solid shape.
export const silhouetteOf = (source) => {
  const { width, height } = source;
  const data = source.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, width, height).data;
  const outside = new Uint8Array(width * height);
  const stack = [];
  const push = (i) => { if (!outside[i] && data[i * 4 + 3] < 128) { outside[i] = 1; stack.push(i); } };
  for (let x = 0; x < width; x++) { push(x); push((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { push(y * width); push(y * width + width - 1); }
  while (stack.length) {
    const i = stack.pop();
    const x = i % width;
    if (x > 0) push(i - 1);
    if (x < width - 1) push(i + 1);
    if (i >= width) push(i - width);
    if (i < width * (height - 1)) push(i + width);
  }
  const out = makeCanvas(width, height);
  const ctx = out.getContext('2d');
  const image = ctx.createImageData(width, height);
  for (let i = 0; i < outside.length; i++) image.data[i * 4 + 3] = outside[i] ? 0 : 255;
  ctx.putImageData(image, 0, 0);
  return out;
};

export const DEFAULT_TRANSFORM = { x: 0, y: 0, scale: 1, stretchX: 1, stretchY: 1, rotation: 0 };

// The transform that centers the source in a w×h grid.
export const centeredTransform = (w, h) => ({ ...DEFAULT_TRANSFORM, x: w / 2, y: h / 2 });

// Grid cells per source pixel when scale is 1: the source just fits inside the grid.
export const baseScale = (source, w, h) => Math.min(w / source.width, h / source.height);

// Applies the transform to a context whose units are grid cells.
export const applyTransform = (ctx, source, w, h, t) => {
  const s = baseScale(source, w, h) * t.scale;
  ctx.translate(t.x, t.y);
  ctx.rotate((t.rotation * Math.PI) / 180);
  ctx.scale(s * t.stretchX, s * t.stretchY);
};

// Average darkness (0 white … 1 black) of every cell.
export const cellDarkness = (source, w, h, t) => {
  const canvas = makeCanvas(w * SAMPLES, h * SAMPLES);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(SAMPLES, 0, 0, SAMPLES, 0, 0);
  applyTransform(ctx, source, w, h, t);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, -source.width / 2, -source.height / 2);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const darkness = new Array(w * h).fill(0);
  for (let y = 0; y < canvas.height; y++) {
    const row = Math.floor(y / SAMPLES) * w;
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      darkness[row + Math.floor(x / SAMPLES)] += 1 - lum / 255;
    }
  }
  return darkness.map(d => d / (SAMPLES * SAMPLES));
};

export const thresholdCells = (darkness, threshold, invert) =>
  darkness.map(d => ((invert ? 1 - d : d) > threshold ? 1 : 0));

// Otsu's threshold over the cell darkness values, clamped away from the extremes.
export const autoThreshold = (darkness) => {
  const bins = 64;
  const hist = new Array(bins).fill(0);
  darkness.forEach(d => { hist[Math.min(bins - 1, Math.floor(d * bins))]++; });
  const total = darkness.length;
  const sumAll = hist.reduce((a, n, i) => a + n * i, 0);
  let sumB = 0, weightB = 0, best = 0, bestT = bins / 2;
  for (let t = 0; t < bins; t++) {
    weightB += hist[t];
    if (!weightB) continue;
    const weightF = total - weightB;
    if (!weightF) break;
    sumB += t * hist[t];
    const between = weightB * weightF * (sumB / weightB - (sumAll - sumB) / weightF) ** 2;
    if (between > best) { best = between; bestT = t; }
  }
  return Math.min(0.9, Math.max(0.1, (bestT + 1) / bins));
};
